import { useEffect, useState, type FormEvent } from "react";
import QRCode from "qrcode";
type Status = { enabled: boolean; recoveryCodesRemaining: number };
export function MfaSettings({
  api,
  mutate,
}: {
  api: <T>(path: string) => Promise<T>;
  mutate: <T>(path: string, body: unknown) => Promise<T>;
}) {
  const [status, setStatus] = useState<Status | null>(null);
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(
    null,
  );
  const [qr, setQr] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    void api<Status>("/admin/mfa")
      .then((value) => {
        if (active) {
          setStatus(value);
          setError("");
        }
      })
      .catch(() => {
        if (active) setError("MFA settings could not load. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [api, attempt]);
  useEffect(() => {
    let active = true;
    setQr("");
    if (setup)
      void QRCode.toDataURL(setup.uri, { width: 240, margin: 2 })
        .then((url) => {
          if (active) setQr(url);
        })
        .catch(() => {
          /* The manual setup key remains available. */
        });
    return () => {
      active = false;
    };
  }, [setup]);
  async function act(
    action: "begin" | "confirm" | "disable" | "recovery-codes",
  ) {
    setBusy(true);
    setError("");
    try {
      const result = await mutate<{
        secret?: string;
        uri?: string;
        enabled?: boolean;
        recoveryCodes?: string[];
      }>(`/admin/mfa/${action}`, {
        currentPassword: password,
        ...(action === "begin" ? {} : { code }),
      });
      if (action === "begin")
        setSetup({ secret: result.secret!, uri: result.uri! });
      else {
        setSetup(null);
        setPassword("");
        setCode("");
        setCodes(result.recoveryCodes ?? []);
        setStatus({
          enabled: !!result.enabled,
          recoveryCodesRemaining: result.recoveryCodes?.length ?? 0,
        });
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    void act(setup ? "confirm" : status?.enabled ? "recovery-codes" : "begin");
  }
  return (
    <section className="cr-panel" aria-labelledby="mfa-title">
      <h2 id="mfa-title">Multi-factor authentication</h2>
      <p>
        {status?.enabled
          ? "On · Your password and an authenticator code protect your staff login."
          : "Add an authenticator code to protect your staff login."}
      </p>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {!status ? (
        <button
          className="cr-button secondary"
          onClick={() => setAttempt((value) => value + 1)}
        >
          Reload MFA settings
        </button>
      ) : codes.length > 0 ? (
        <>
          <h3>Save your recovery codes</h3>
          <p>
            Each code works once in place of an authenticator code. Keep these
            somewhere safe, separate from your password. They will not be shown
            again.
          </p>
          <ul className="cr-mfa-codes">
            {codes.map((value) => (
              <li key={value}>
                <code>{value}</code>
              </li>
            ))}
          </ul>
          <button className="cr-button" onClick={() => setCodes([])}>
            I have saved my recovery codes
          </button>
        </>
      ) : (
        <form className="cr-form" onSubmit={submit}>
          {setup && (
            <>
              <h3>Connect your authenticator</h3>
              <p>
                In your authenticator app, add an account and scan this QR code.
                Setup expires in 10 minutes.
              </p>
              {qr && (
                <img
                  src={qr}
                  alt="Scan this QR code with your authenticator app"
                  width={240}
                  height={240}
                />
              )}
              <details>
                <summary>Enter a setup key instead</summary>
                <p>Account: Cache River Telephone · Time-based · 6 digits</p>
                <code className="cr-mfa-secret">{setup.secret}</code>
              </details>
              <p>Enter the six-digit code from your app to turn MFA on.</p>
            </>
          )}
          <label>
            Current password
            <input
              type="password"
              autoComplete="current-password"
              required
              maxLength={128}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </label>
          {(setup || status.enabled) && (
            <label>
              {setup
                ? "Authenticator code"
                : "Authenticator or unused recovery code"}
              <input
                autoComplete="one-time-code"
                inputMode={setup ? "numeric" : "text"}
                required
                maxLength={50}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                disabled={busy}
              />
            </label>
          )}
          {status.enabled && (
            <p>
              {status.recoveryCodesRemaining} recovery codes remain. Replacing
              them invalidates all previous recovery codes. MFA changes sign out
              your other sessions.
            </p>
          )}
          <button className="cr-button" disabled={busy}>
            {busy
              ? "Saving…"
              : setup
                ? "Verify and enable MFA"
                : status.enabled
                  ? "Replace recovery codes"
                  : "Set up authenticator"}
          </button>
          {setup && (
            <button
              type="button"
              className="cr-button secondary"
              disabled={busy}
              onClick={() => {
                setSetup(null);
                setPassword("");
                setCode("");
              }}
            >
              Cancel setup
            </button>
          )}
          {status.enabled && (
            <details>
              <summary>Turn off MFA</summary>
              <p>
                Your next sign-in will only require your password. Enter your
                current password and a fresh authenticator or recovery code
                above to confirm.
              </p>
              <button
                type="button"
                className="cr-button secondary"
                disabled={busy || !password || !code}
                onClick={() => void act("disable")}
              >
                Verify and turn off MFA
              </button>
            </details>
          )}
        </form>
      )}
    </section>
  );
}
